import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const PublicLayout = ({ children, onGetStarted }) => {
  const location = useLocation();
  
  const navLinks = [
    { path: '/how-it-works', label: 'How it works' },
    { path: '/features', label: 'Features' },
    { path: '/pricing', label: 'Pricing' },
    { path: '/knowledge-base', label: 'Knowledge Base' },
    { path: '/about', label: 'About' },
  ];

  return (
    <div className="min-h-screen bg-[var(--color-bg)]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4">
        <div className="max-w-6xl mx-auto">
          <div className="card px-6 py-3 flex items-center justify-between" style={{backdropFilter: 'blur(12px)', background: 'rgba(255,255,255,0.85)'}}>
            <Link to="/" className="flex items-center gap-2">
              <img src="/stilos-logo.png" alt="Stilos" className="w-8 h-8 rounded-lg object-cover" />
              <span className="font-display font-semibold text-base tracking-tight">Stilos</span>
            </Link>
            <div className="hidden lg:flex items-center gap-6">
              {navLinks.map(link => (
                <Link key={link.path} to={link.path}
                  className={`text-sm transition-colors ${location.pathname === link.path ? 'text-[var(--color-charcoal)] font-medium' : 'text-[var(--color-muted)] hover:text-[var(--color-charcoal)]'}`}>
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <button onClick={onGetStarted} className="hidden md:inline-flex text-sm text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">Log in</button>
              <button onClick={onGetStarted} className="btn btn-primary text-sm">Start free</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-24">{children}</div>

      {/* Footer */}
      <footer className="py-16 px-6 border-t border-[var(--color-border)]">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-5 gap-10 mb-12">
            {/* Brand */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <img src="/stilos-logo.png" alt="Stilos" className="w-7 h-7 rounded-lg object-cover" />
                <span className="font-display font-semibold text-sm">Stilos</span>
              </div>
              <p className="text-xs text-[var(--color-muted)] leading-relaxed mb-3">
                AI-powered content creation for small businesses. Part of Themelio.
              </p>
              <p className="text-[10px] text-[var(--color-muted)]">Your voice. AI-ready.</p>
            </div>

            {/* Product */}
            <div>
              <h4 className="font-display text-xs font-semibold mb-4 text-[var(--color-charcoal)]">Product</h4>
              <div className="space-y-2.5">
                <Link to="/features" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">Features</Link>
                <Link to="/pricing" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">Pricing</Link>
                <Link to="/how-it-works" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">How it Works</Link>
                <Link to="/roadmap" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">Roadmap</Link>
                <Link to="/system-status" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">System Status</Link>
              </div>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-display text-xs font-semibold mb-4 text-[var(--color-charcoal)]">Company</h4>
              <div className="space-y-2.5">
                <Link to="/about" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">About</Link>
                <Link to="/contact" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">Contact</Link>
                <Link to="/impressum" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">Impressum</Link>
              </div>
            </div>

            {/* Resources */}
            <div>
              <h4 className="font-display text-xs font-semibold mb-4 text-[var(--color-charcoal)]">Resources</h4>
              <div className="space-y-2.5">
                <Link to="/knowledge-base" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">Knowledge Base</Link>
                <Link to="/appsumo" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">AppSumo Deal</Link>
              </div>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-display text-xs font-semibold mb-4 text-[var(--color-charcoal)]">Legal</h4>
              <div className="space-y-2.5">
                <Link to="/privacy" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">Privacy Policy</Link>
                <Link to="/terms" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">Terms of Service</Link>
                <Link to="/cookies" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">Cookie Policy</Link>
                <Link to="/dpa" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">Data Processing</Link>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-[var(--color-border)] flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-[var(--color-muted)]">&copy; 2026 Stilos by Themelio. All rights reserved.</p>
            <p className="text-[10px] text-[var(--color-muted)]">Built in the EU. GDPR compliant.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
